import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonTitle,
  IonContent,
  useIonRouter,
  useIonViewDidEnter,
} from '@ionic/react';
import { shareOutline, shareSocialOutline } from 'ionicons/icons';
import SnowballMenu from '@/modules/user/SnowballMenu';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { useTopicState } from './TopicStateContext';
import { startShareSession } from '@/modules/share/session_storage';
import { selectTopic, selectTopicMutations } from './reducer';
import TopicChangeList from './TopicChangeList';
import type { TopicMutation } from './mutations';
import { pxToRem } from '@/modules/config/constants';

const subheadingStyle: React.CSSProperties = {
  fontSize: pxToRem(20),
  fontWeight: 600,
  margin: '16px 16px 4px',
  wordBreak: 'break-word',
};

/**
 * Full-page, chronologically-ordered history of the field changes made to an
 * topic during this session. Each change is a collapsible row; the list starts
 * scrolled to the bottom so the most recent change is in view (#418).
 *
 * Each row's header carries a Share Updates button (#427): the tapped row
 * becomes the head of the change list shared through the wizard's update flow.
 */
const TopicChangesPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const { state } = useTopicState();
  const router = useIonRouter();
  const mutations = selectTopicMutations(state, topicId);
  const topicTitle = selectTopic(state, topicId)?.title;
  const contentRef = useRef<HTMLIonContentElement>(null);

  // Start at the bottom (newest) once the view has fully entered. Optional-chain
  // the method so a non-Ionic element in tests doesn't throw.
  useIonViewDidEnter(() => {
    contentRef.current?.scrollToBottom?.(0);
  });

  const shareUpdatesFrom = async (mutation: TopicMutation) => {
    // Bootstrap the update-flow wizard session (the change head is the "Since"
    // and the mode signal), then route — persisted state carries it, not the URL
    // (#427). The sender types their own summary, so none is seeded here.
    await startShareSession(topicId, {
      update: { aboutChangesSince: mutation.at },
    });
    router.push(
      `/topics/${encodeURIComponent(topicId)}/share`,
      'forward',
      'push'
    );
  };

  const renderRowEnd = (mutation: TopicMutation) => (
    <ActionGuard
      disableMode="hidden"
      action={ACTIONS.SHARE}
      resource={RESOURCES.TOPICS}
      resourceId={topicId}
      guardedElem={
        <IonButton
          fill="clear"
          slot="end"
          data-testid="share-updates-from-change"
          onClick={e => {
            // Don't toggle the row open/closed.
            e.stopPropagation();
            void shareUpdatesFrom(mutation);
          }}
        >
          <IonIcon
            ios={shareOutline}
            md={shareSocialOutline}
            slot="icon-only"
            aria-hidden="true"
          />
        </IonButton>
      }
    />
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/topics/${topicId}`} />
          </IonButtons>
          <IonTitle>Changes</IonTitle>
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef}>
        {topicTitle && <h1 style={subheadingStyle}>{topicTitle}</h1>}
        <ActionGuardContainer>
          <TopicChangeList
            mutations={mutations}
            emptyMessage="No changes have been made to this topic yet."
            renderRowEnd={renderRowEnd}
          />
        </ActionGuardContainer>
      </IonContent>
    </IonPage>
  );
};

export default TopicChangesPage;
